import React, { useCallback } from 'react';
import { Stack, Typography } from '@mui/material';
import { KnowledgePanel } from 'new/elements/KnowledgePanel';
import { AclSelection } from 'new/elements/AclSelection';
import { RefreshRoundedIconButton } from 'new/elements/Button/RefreshRoundedIconButton';
import { LastRequestTimeIndicator } from 'new/components/LastRequestTimeIndicator';
import CardsListPageNumbers from 'js/components/elements/cards/CardsListPageNumbers';
import Tooltip from 'js/components/elements/mui/Tooltip';
import { renderFeatureBadge } from 'js/constants/featureBadges';

import styles from './FlowRunsHeader.module.scss';

interface RunScreenHeaderProps {
  isLoading: boolean;
  meta: {
    currentPage: number;
    totalCount: number;
    pageCount: number;
    totalPageCount: number;
  };
  lastUpdated: number;
  onRefresh: any;
  onAclChange?: (value: string) => void;
}

function FlowRunsHeader(props: RunScreenHeaderProps): JSX.Element {
  const { meta, lastUpdated, isLoading: loading, onRefresh: refresh, onAclChange } = props;

  const { currentPage, totalPageCount, totalCount } = meta ?? {};

  const dispatchPageChange = useCallback((page: number = 1) => refresh({ page }), [refresh]);

  const dispatchAclChange = useCallback(
    (value: string) => {
      onAclChange?.(value);
      requestAnimationFrame(() => {
        if (window?.history?.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.set('page', '1');
          window.history.replaceState({}, '', url.toString());
        }
        dispatchPageChange(1);
      });
    },
    [onAclChange, dispatchPageChange],
  );

  const selectedPage = Number(currentPage);

  return (
    <header className={styles.container} role="banner" aria-label="Flow Runs Header">
      <Stack direction="row" alignItems="center" gap={2}>
        <KnowledgePanel
          alwaysShowUnderline
          knowledgeContent={
            <Typography sx={{ p: 2 }}>
              Visualize the latest run for each of your data flows, sorted by start time (newest
              first). Select any run to see its details, or use 'Flow Insights' to explore
              historical run data for that flow.
              <br />
              <br />
              {renderFeatureBadge('FLOW_RUNS', {
                size: 'large',
                variant: 'text',
              })}
              {'    '}
              We're fine-tuning this dashboard to make it even better. Have thoughts, ideas or
              spotted something?{' '}
              <a
                href={`mailto:product@nexla.com?subject=Product Feedback ~ Flow Runs Dashboard`}
                rel="noopener noreferrer"
              >
                Drop us a note!
              </a>
            </Typography>
          }
        >
          <Stack direction="row" alignItems="center" gap={2}>
            <Typography variant="subheading" component="h1">
              Flow Runs
            </Typography>
          </Stack>
        </KnowledgePanel>
        {/* @ts-ignore: Tooltip is a JS file and TS doesn't know its props */}
        <Tooltip
          tooltipTheme="fieldInfo"
          placement="bottom-start"
          title={loading ? null : <LastRequestTimeIndicator lastRequestTime={lastUpdated} />}
        >
          <div>
            <RefreshRoundedIconButton
              showTooltip={false}
              isLoading={loading}
              onRefreshClicked={() => dispatchPageChange(selectedPage)}
            />
          </div>
        </Tooltip>
        {renderFeatureBadge('FLOW_RUNS', {
          size: 'medium',
        })}
      </Stack>
      <Stack direction="row" alignItems="center" marginLeft={'auto'} justifyContent={'flex-start'}>
        <CardsListPageNumbers
          isLoading={loading}
          currentPage={selectedPage}
          setCurrentPage={dispatchPageChange}
          totalPages={totalPageCount ?? 1}
          totalItems={totalCount ?? 0}
        />
      </Stack>
      <Stack direction="row" alignItems="center" marginLeft={'40px'}>
        <AclSelection onChange={dispatchAclChange} aria-label="Access Control List Selection" />
      </Stack>
    </header>
  );
}

const arePropsEqual = (
  prevProps: RunScreenHeaderProps,
  nextProps: RunScreenHeaderProps,
): boolean => {
  return (
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.lastUpdated === nextProps.lastUpdated &&
    prevProps.onRefresh === nextProps.onRefresh &&
    prevProps.onAclChange === nextProps.onAclChange &&
    prevProps.meta?.currentPage === nextProps.meta?.currentPage &&
    prevProps.meta?.totalCount === nextProps.meta?.totalCount &&
    prevProps.meta?.pageCount === nextProps.meta?.pageCount &&
    prevProps.meta?.totalPageCount === nextProps.meta?.totalPageCount
  );
};

const FlowRunsHeaderMemo = React.memo(FlowRunsHeader, arePropsEqual);
FlowRunsHeaderMemo.displayName = 'FlowRunsHeader';

export { FlowRunsHeaderMemo, FlowRunsHeader };
